import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mail,
  ArrowDownLeft,
  ArrowUpRight,
  ExternalLink,
  Bot,
  MapPin,
  MapPinOff,
  Trash2,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useActivityStore } from '../../store/activityStore';
import { TemperatureIcon } from '../shared/temperatureConfig';
import type { Activity, ActivityStatus } from '../../types';
import { AddActivityModal } from './AddActivityModal';
import {
  ACTIVITY_ICONS,
  ACTIVITY_COLORS,
  EMAIL_DIRECTION_COLORS,
  MEETING_LOCATION_LABELS,
  formatFullTimestamp,
  isEmailWithMetadata,
} from './activityVisuals';
import { gmailSearchUrl } from './gmailLink';

interface ActivityDetailProps {
  activity: Activity;
  canDelete: boolean;
  onClose: () => void;
}

// Humanize an UPPER_SNAKE enum value: "ON_CAMPUS" -> "On campus" (mirrors ActivityTimeline)
const humanizeEnum = (value: string): string => {
  const lower = value.toLowerCase().replace(/_/g, ' ');
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

// Status pill styling per ActivityStatus
const STATUS_PILL: Record<ActivityStatus, { label: string; className: string }> = {
  COMPLETED: {
    label: 'Completed',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
  PLANNED: {
    label: 'Planned',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  CANCELLED: {
    label: 'Cancelled',
    className: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  },
};

// A small labelled cell in the meta grid
const MetaCell = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <dt className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
      {label}
    </dt>
    <dd className="mt-0.5 text-sm text-gray-700 dark:text-gray-300">{children}</dd>
  </div>
);

/**
 * Full activity detail content — rendered inside the SlideOver body (Task 8 wraps it).
 * Adapts by activity type and offers Complete / Cancel / Delete actions in the footer.
 */
export const ActivityDetail = ({ activity, canDelete, onClose }: ActivityDetailProps) => {
  const { deleteActivity, cancelActivity, isSaving } = useActivityStore();
  const [completing, setCompleting] = useState(false);

  const person = activity.person;
  const personId = person?.id ?? null;
  const personName = person
    ? `${person.crmFirstName || person.firstName} ${person.crmLastName || person.lastName}`.trim()
    : '';

  const Icon = ACTIVITY_ICONS[activity.type];
  const colorClass = ACTIVITY_COLORS[activity.type];
  const statusPill = STATUS_PILL[activity.status];

  const isEmail = isEmailWithMetadata(activity);
  const isInbound = activity.emailDirection === 'INBOUND';
  const gmailUrl = gmailSearchUrl(activity.rfc822MessageId);

  const meetingLocationLabel = activity.meetingLocation
    ? MEETING_LOCATION_LABELS[activity.meetingLocation] ?? activity.meetingLocation
    : null;

  // "When" reflects the status-appropriate timestamp (spec §3.2): a PLANNED activity's
  // activityAt is its creation time, so prefer scheduledAt; COMPLETED prefers completedAt.
  // Falls back to activityAt (guarantees a non-null string).
  const whenTimestamp =
    (activity.status === 'PLANNED' && activity.scheduledAt) ||
    (activity.status === 'COMPLETED' && activity.completedAt) ||
    activity.activityAt;

  // Handle Cancel (planned activity)
  const handleCancel = async () => {
    if (!personId) return;
    const reason = window.prompt('Reason (optional)');
    // window.prompt returns null on dismiss — treat as "do not cancel"
    if (reason === null) return;
    try {
      await cancelActivity(personId, activity.id, reason || undefined);
      toast.success('Activity cancelled');
      onClose();
    } catch {
      // Error toast already surfaced via store
    }
  };

  // Handle Delete
  const handleDelete = async () => {
    if (!personId) return;
    if (!window.confirm('Are you sure you want to delete this activity?')) return;
    try {
      await deleteActivity(personId, activity.id);
      toast.success('Activity deleted');
      onClose();
    } catch {
      // Error toast already surfaced via store
    }
  };

  return (
    <div className="flex flex-col">
      {/* Header / contact block */}
      <div className="flex items-start gap-3 border-b border-gray-100 px-4 py-4 dark:border-gray-700 sm:px-6">
        {/* Type icon */}
        <div
          className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full ${colorClass}`}
        >
          {isEmail ? <Mail className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
        </div>

        <div className="min-w-0 flex-1">
          {/* Type label + status pill */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {humanizeEnum(activity.type)}
            </span>
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusPill.className}`}
            >
              {statusPill.label}
            </span>
            {activity.isAutoLogged && (
              <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                <Bot className="h-3 w-3" />
                Synced
              </span>
            )}
          </div>

          {/* Contact name + temperature + subtitle */}
          {person && (
            <div className="mt-1">
              <div className="flex items-center gap-1.5">
                {person.temperature && (
                  <TemperatureIcon temperature={person.temperature} className="h-4 w-4 flex-shrink-0" />
                )}
                <Link
                  to={`/people/${person.id}`}
                  className="truncate font-medium text-blast-600 hover:text-blast-700 dark:text-blast-400 dark:hover:text-blast-300"
                >
                  {personName}
                </Link>
              </div>
              {(person.position || person.organizationName) && (
                <p className="mt-0.5 truncate text-sm text-gray-500 dark:text-gray-400">
                  {[person.position, person.organizationName].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="space-y-5 px-4 py-4 sm:px-6">
        {isEmail ? (
          /* Email with synced metadata */
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                  activity.emailDirection ? EMAIL_DIRECTION_COLORS[activity.emailDirection] : ACTIVITY_COLORS.EMAIL
                }`}
              >
                {isInbound ? <ArrowDownLeft className="h-3 w-3" /> : <ArrowUpRight className="h-3 w-3" />}
                {isInbound ? 'Inbound' : 'Outbound'}
              </span>
            </div>

            {activity.emailSubject && (
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {activity.emailSubject}
              </p>
            )}

            <div className="space-y-0.5">
              {activity.emailFrom && (
                <p className="text-xs text-gray-500 dark:text-gray-400 break-words">
                  <span className="font-medium">From:</span> {activity.emailFrom}
                </p>
              )}
              {activity.emailTo && (
                <p className="text-xs text-gray-500 dark:text-gray-400 break-words">
                  <span className="font-medium">To:</span> {activity.emailTo}
                </p>
              )}
            </div>

            {activity.emailSnippet && (
              <p className="whitespace-pre-wrap break-words text-sm text-gray-700 dark:text-gray-300">
                {activity.emailSnippet}
              </p>
            )}

            {gmailUrl && (
              <a
                href={gmailUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                <ExternalLink className="h-4 w-4" />
                View in Gmail
              </a>
            )}
          </div>
        ) : (
          /* Notes (default for NOTE / CALL / TEXT / IN_PERSON / OTHER / plain EMAIL) */
          <div>
            <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
              Notes
            </h3>
            <p className="whitespace-pre-wrap break-words text-sm text-gray-700 dark:text-gray-300">
              {activity.summary || <span className="italic text-gray-400">No notes</span>}
            </p>
          </div>
        )}

        {/* CALL: disposition */}
        {activity.type === 'CALL' && activity.callDisposition && (
          <div>
            <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
              Disposition
            </h3>
            <span className="inline-flex items-center rounded px-2 py-0.5 text-sm font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
              {humanizeEnum(activity.callDisposition)}
            </span>
          </div>
        )}

        {/* IN_PERSON: meeting location + location capture */}
        {activity.type === 'IN_PERSON' && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              {meetingLocationLabel && (
                <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400">
                  {meetingLocationLabel}
                </span>
              )}
              {activity.hasLocation === false && (
                <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400">
                  <MapPinOff className="h-3 w-3" />
                  No location
                </span>
              )}
            </div>

            {/* Verification verdict (manager/creator only — presence keys visibility) */}
            {activity.verification ? (
              <p className={`inline-flex items-center gap-1 text-xs ${
                activity.verification.state === 'NEEDS_REVIEW'
                  ? 'text-rose-600 dark:text-rose-400'
                  : activity.verification.state === 'VERIFIED'
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-gray-500 dark:text-gray-400'
              }`}>
                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                {activity.verification.state === 'NEEDS_REVIEW' && (
                  <>🚩 Needs review{activity.verification.distanceMiles != null && <> · {activity.verification.distanceMiles.toFixed(1)} mi from {activity.person?.organizationName ?? 'campus'}</>}</>
                )}
                {activity.verification.state === 'VERIFIED' && (
                  <>✅ Verified{activity.verification.distanceMiles != null && <> · {activity.verification.distanceMiles.toFixed(1)} mi from {activity.person?.organizationName ?? 'campus'}</>}</>
                )}
                {activity.verification.state === 'UNVERIFIED' && (
                  <>⚪ Unverified{activity.verification.reason === 'NO_GPS' ? ' · no GPS' : ' · school not geocoded'}</>
                )}
              </p>
            ) : activity.latitude != null && activity.longitude != null && (
              <p className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                Location captured
                {activity.locationAccuracy != null && (
                  <span> · ±{Math.round(activity.locationAccuracy)}m</span>
                )}
              </p>
            )}
          </div>
        )}

        {/* CANCELLED: reason */}
        {activity.status === 'CANCELLED' && activity.cancelReason && (
          <div>
            <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
              Cancellation reason
            </h3>
            <p className="whitespace-pre-wrap break-words text-sm text-gray-700 dark:text-gray-300">
              {activity.cancelReason}
            </p>
          </div>
        )}

        {/* Meta grid */}
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <MetaCell label="When">{formatFullTimestamp(whenTimestamp)}</MetaCell>
          <MetaCell label="Type">{humanizeEnum(activity.type)}</MetaCell>
          <MetaCell label="Logged by">
            {activity.createdBy.displayName || activity.createdBy.email}
          </MetaCell>
        </dl>
      </div>

      {/* Footer actions */}
      <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 bg-gray-50 px-4 py-4 dark:border-gray-700 dark:bg-gray-800/50 sm:px-6">
        {activity.status === 'PLANNED' && personId && (
          <>
            <button
              type="button"
              onClick={() => setCompleting(true)}
              disabled={isSaving}
              className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <CheckCircle2 className="h-4 w-4" />
              Complete
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSaving}
              className="btn-secondary inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <XCircle className="h-4 w-4" />
              Cancel
            </button>
          </>
        )}

        {/* Delete pushed to the right; only when allowed */}
        {canDelete && personId && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={isSaving}
            className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-gray-400 transition-colors hover:text-red-600 disabled:opacity-50 dark:hover:text-red-400"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        )}
      </div>

      {/* Complete flow — reuses AddActivityModal in completing mode (prefill.completingActivityId) */}
      {personId && (
        <AddActivityModal
          isOpen={completing}
          onClose={() => setCompleting(false)}
          personId={personId}
          personName={personName}
          currentTemperature={person?.temperature ?? null}
          organizationName={person?.organizationName ?? null}
          prefill={{ type: activity.type, completingActivityId: activity.id }}
          onActivityCreated={() => {
            setCompleting(false);
            onClose();
          }}
        />
      )}
    </div>
  );
};
